import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, LayoutIcon, SettingsIcon, SmartphoneIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { PageCTA } from '../components/PageCTA';
import { ProblemSolution } from '../components/ProblemSolution';
import { SinglePlanOffer } from '../components/ui/SinglePlanOffer';
import { TestimonialsSection, type Testimonial } from '../components/ui/TestimonialsSection';
const services = [{
  icon: LayoutIcon,
  number: '01',
  title: 'Página web profesional',
  description: 'Una presencia clara y alineada con tu negocio para explicar qué haces y por qué importa.'
}, {
  icon: SmartphoneIcon,
  number: '02',
  title: 'Carta digital inteligente',
  description: 'Una experiencia móvil para presentar tu oferta sin PDFs, fricción ni información desactualizada.'
}, {
  icon: SettingsIcon,
  number: '03',
  title: 'Gestión incluida',
  description: 'Una base preparada para acompañarte después de publicar, con soporte y actualizaciones.'
}];
const principles = [['01', 'Claridad', 'Mensajes, jerarquía y acciones que el usuario entiende sin esfuerzo.'], ['02', 'Rendimiento', 'Una experiencia ligera, legible y adaptada a cada pantalla.'], ['03', 'Continuidad', 'Una presencia preparada para seguir siendo útil cuando tu negocio evoluciona.']];
const references: Testimonial[] = [{
  name: 'Negocio local',
  role: 'Referencia ilustrativa',
  text: 'El valor está en dejar de tener información repartida y contar con un lugar que explica el negocio de forma ordenada.'
}, {
  name: 'Empresa de servicios',
  role: 'Referencia ilustrativa',
  text: 'Una presencia clara permite que una primera visita llegue a una conversación con mucho mejor contexto.'
}, {
  name: 'Proyecto de hostelería',
  role: 'Referencia ilustrativa',
  text: 'Una carta digital bien pensada reduce preguntas repetidas y hace más simple elegir desde el móvil.'
}, {
  name: 'Marca personal',
  role: 'Referencia ilustrativa',
  text: 'Cuando el mensaje y la imagen trabajan juntos, es más fácil transmitir criterio antes de la primera llamada.'
}];
export function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="bg-black" data-id="element-482" data-mp="28r3lw56ucijx">
      <Hero data-id="element-483" data-mp="28o7ii1c851o3" />
      <ProblemSolution data-id="element-484" data-mp="23r8u7zf8roxt" />

      <section className="section-shell border-t border-white/10 bg-[#0a0a0a]" aria-labelledby="system-title" data-id="element-485" data-mp="23s0dcarnju77">
        <div className="container mx-auto px-6 md:px-12" data-id="element-486" data-mp="2ihoenbzu2sux">
          <div className="grid gap-8 border-b border-white/10 pb-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-487" data-mp="2dov9wo4d7kiu">
            <div data-id="element-488" data-mp="1pz124kywkm5g">
              <p className="eyebrow" data-id="element-489" data-mp="1pyj35gkuvttu">El sistema</p>
              <h2 id="system-title" className="mt-5 text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-490" data-mp="1l0zgpte08aqt">
                Las piezas adecuadas. Sin complejidad innecesaria.
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-400 sm:text-base" data-id="element-491" data-mp="5aq9tmpogbf5">
              Un sistema digital debe explicar, orientar y permanecer útil. Por
              eso conectamos web, carta y gestión bajo una misma lógica.
            </p>
          </div>
          <div className="grid border-l border-t border-white/10 md:grid-cols-3" data-id="element-492" data-mp="jahqxwbhq7xd">
            {services.map((service, index) => {
            const Icon = service.icon;
            return <motion.article key={service.number} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.3
            }} transition={{
              duration: 0.45,
              delay: index * 0.06
            }} className="group flex min-h-[260px] flex-col border-r border-b border-white/10 bg-black p-7 sm:p-8" data-id="element-493" data-mp="a7qdxs2yv43m" data-mpi={index}>
                  <div className="flex items-start justify-between" data-id="element-494" data-mp="30jcb9x0y9qtc">
                    <Icon size={22} className="text-white" aria-hidden="true" data-id="element-495" data-mp="20xaffkpx03j" />
                    <span className="text-[10px] font-medium tracking-[0.2em] text-neutral-600" data-id="element-496" data-mp="1yumd134lh1g">
                      {service.number}
                    </span>
                  </div>
                  <h3 className="mt-12 text-lg font-medium tracking-[-0.02em] text-white" data-id="element-497" data-mp="3o6c2wkuflqj4">
                    {service.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-498" data-mp="2cthzlisda8ao">
                    {service.description}
                  </p>
                  <Link to="/servicios" className="mt-auto inline-flex w-fit items-center gap-2 pt-8 text-sm text-neutral-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-499" data-mp="28pttz5napms2">
                    Ver detalle <ArrowRightIcon size={15} aria-hidden="true" data-id="element-500" data-mp="x8hic5bg6mdp" />
                  </Link>
                </motion.article>;
          })}
          </div>
        </div>
      </section>

      <SinglePlanOffer data-id="element-501" data-mp="16zc2931819vz" />

      <section className="section-shell border-t border-white/10 bg-black" aria-labelledby="principles-title" data-id="element-502" data-mp="1726n2vo5cwpx">
        <div className="container mx-auto px-6 md:px-12" data-id="element-503" data-mp="1g00js7ptpsdm">
          <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-504" data-mp="1fyfk63onjj8w">
            <div data-id="element-505" data-mp="1kz3diw0csc6a">
              <p className="eyebrow" data-id="element-506" data-mp="1kxiqdrgdbvig">El criterio</p>
              <h2 id="principles-title" className="mt-5 text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-507" data-mp="3hsyfqcewd12s">
                Una experiencia pensada para facilitar decisiones.
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-400 sm:text-base" data-id="element-508" data-mp="oxofbxchzcio">
              No prometemos cifras sin contexto. Diseñamos los factores que
              ayudan a una presencia digital a ser más comprensible, confiable y
              accionable.
            </p>
          </div>
          <div className="mt-10 grid overflow-hidden border border-white/10 sm:grid-cols-3" data-id="element-509" data-mp="1cuu5vmltgxjp">
            {principles.map(([number, title, detail]) => <div key={number} className="border-b border-white/10 p-7 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0" data-id="element-510" data-mp="17w0t5wectakl">
                <p className="text-[10px] font-medium tracking-[0.2em] text-neutral-600" data-id="element-511" data-mp="1hmlys3sg3fd0">
                  {number}
                </p>
                <h3 className="mt-10 text-lg font-medium text-white" data-id="element-512" data-mp="1vjiqe7ge10uy">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-513" data-mp="2a8ttxyybnbet">
                  {detail}
                </p>
              </div>)}
          </div>
          <Link to="/resultados" className="link-arrow mt-8" data-id="element-514" data-mp="2f5arfjrev24k">
            Ver nuestro enfoque <ArrowRightIcon size={15} aria-hidden="true" data-id="element-515" data-mp="20a4lsxw895br" />
          </Link>
        </div>
      </section>

      <TestimonialsSection testimonials={references} data-id="element-516" data-mp="257mmy2406g97" />
      <PageCTA data-id="element-517" data-mp="1rhst15dn55sm" />
    </div>;
}